/**
 * Ratchet sessions for chat (ADR-016 stage 5).
 *
 * One session per pair of DEVICES, not per pair of people. Somebody with a
 * phone and a laptop, talking to somebody with two devices, is four sessions,
 * and a message goes into every one of them — plus into this account's own
 * other devices, or the sender never sees on their phone what they wrote on
 * their laptop. That fan-out is the price ADR-011 §10 named and ADR-016 accepts.
 *
 * Storage and the API are injected, so this whole file runs under node --test
 * with no IndexedDB and no network.
 */
import nacl from 'tweetnacl';
import {
  x3dhInitiate, x3dhRespond, initSender, initReceiver,
  ratchetEncrypt, ratchetDecrypt, serializeState, deserializeState,
  generatePreKey, signPreKey, verifyPreKey, associatedData,
} from './doubleRatchet.mjs';

export const MSG_PREKEY = 0x01;   // v1 handshake: unsigned, refused on receipt
export const MSG_NORMAL = 0x02;   // the session is already up
export const MSG_SELF   = 0x03;   // own copy, LEGACY: one eternal key (read-only era)
export const MSG_PREKEY2 = 0x04;  // v2 handshake: carries the initiator's signature
export const MSG_SELF2  = 0x05;   // own copy v2: one key per message, spent on reading

export const SELF_COPY_TAG = 'VoxioxSelfCopyV1';
export const SELF_TAG_MSG = 'VoxioxSelfMsgV2';
export const SELF_TAG_CHAIN = 'VoxioxSelfChainV2';

/**
 * How long an unclaimed self-copy key waits for its echo, and how many may
 * wait at once — the skipped-key discipline applied to our own mail
 * (SEC-07, chat-message-lifecycle §7д). Seven days matches the server's
 * sync window: an echo that has not come back by then is not coming.
 */
export const SELF_PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_SELF_PENDING = 2000;

/**
 * A signature made here must never validate in any other context — and the
 * recipient's DH identity is inside the transcript, so a handshake signed for
 * one person does not verify when the server re-mails it to another.
 */
export const PREKEY_ENVELOPE_TAG = 'VoxioxPrekeySigV2';

/** Keep enough that a friend can start talking while we are offline. */
export const ONE_TIME_TARGET = 100;

/**
 * How many replaced sessions to keep, and for how long.
 *
 * They exist only to open messages that were already in flight when a session
 * was rebuilt. Two is enough for a reset storm; a week matches how long a
 * skipped key lives, and for the same reason — a stored session is a stored
 * way to read old messages.
 */
export const MAX_ARCHIVED_SESSIONS = 2;
export const ARCHIVED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const ONE_TIME_LOW_WATER = 20;

const b64 = (u8) => btoa(String.fromCharCode(...u8));
function unb64(s) {
  const bin = atob(s); const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
function concat(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}
function equal(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  let d = 0; for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

/**
 * What the initiator's signature covers — everything that names the parties.
 *
 * A prekey message used to carry the initiator's DH key on the wire, X3DH
 * derived straight from it, and WHO sent it came from a database row the
 * server owns. Nothing tied the two together (NEW-SEC-01). The transcript
 * binds the signing identity, the DH identity, the ephemeral, and the
 * RECIPIENT's DH identity into one signature: a hostile server can still sign
 * a handshake with its own key, but it can no longer put somebody else's name
 * on it — the verifier compares the signing key on the wire against the pin —
 * and it cannot replay a signed handshake to a different recipient.
 */
export function prekeyTranscript({ identitySigPub, identityDhPub, ephemeralPub,
                                   recipientIdentityDhPub, signedPreKeyPub, oneTimePreKeyPub }) {
  return concat(
    new TextEncoder().encode(PREKEY_ENVELOPE_TAG),
    identitySigPub, identityDhPub, ephemeralPub, recipientIdentityDhPub, signedPreKeyPub,
    new Uint8Array([oneTimePreKeyPub ? 1 : 0]),
    oneTimePreKeyPub || new Uint8Array(0),
  );
}

/**
 * Wrap a ratchet frame with what the other side needs to open it.
 *
 * A first message has to say which of the recipient's prekeys it used —
 * they hold several and cannot guess — and carry the ephemeral that half the
 * X3DH terms depend on. Everything here is public; none of it is a secret
 * leaked by being on the wire. `sig` is the initiator's signature over
 * prekeyTranscript(); the type byte is the version, as the format rule in
 * the brief demands.
 */
export function packPreKeyMessage({ identitySigPub, identityDhPub, ephemeralPub,
                                    signedPreKeyPub, oneTimePreKeyPub, sig, frame }) {
  return concat(
    new Uint8Array([MSG_PREKEY2]), identitySigPub, identityDhPub, ephemeralPub, signedPreKeyPub,
    new Uint8Array([oneTimePreKeyPub ? 1 : 0]),
    oneTimePreKeyPub || new Uint8Array(0),
    sig,
    frame,
  );
}

export function packNormalMessage(frame) {
  return concat(new Uint8Array([MSG_NORMAL]), frame);
}

export function unpackMessage(bytes) {
  if (!bytes || bytes.length < 2) return null;
  if (bytes[0] === MSG_NORMAL) return { type: MSG_NORMAL, frame: bytes.slice(1) };
  if (bytes[0] === MSG_PREKEY) {
    // The unsigned v1 layout. Still parsed — openMessage refuses it with a
    // reason a person can act on, which "neither prekey nor normal" is not.
    if (bytes.length < 98) return null;
    const hasOpk = bytes[97] === 1;
    const off = 98 + (hasOpk ? 32 : 0);
    if (bytes.length < off) return null;
    return {
      type: MSG_PREKEY, v: 1,
      identityDhPub: bytes.slice(1, 33),
      ephemeralPub: bytes.slice(33, 65),
      signedPreKeyPub: bytes.slice(65, 97),
      oneTimePreKeyPub: hasOpk ? bytes.slice(98, 130) : null,
      frame: bytes.slice(off),
    };
  }
  if (bytes[0] !== MSG_PREKEY2 || bytes.length < 130) return null;
  const hasOpk = bytes[129] === 1;
  const sigOff = 130 + (hasOpk ? 32 : 0);
  if (bytes.length < sigOff + 64) return null;
  return {
    type: MSG_PREKEY, v: 2,
    identitySigPub: bytes.slice(1, 33),
    identityDhPub: bytes.slice(33, 65),
    ephemeralPub: bytes.slice(65, 97),
    signedPreKeyPub: bytes.slice(97, 129),
    oneTimePreKeyPub: hasOpk ? bytes.slice(130, 162) : null,
    sig: bytes.slice(sigOff, sigOff + 64),
    frame: bytes.slice(sigOff + 64),
  };
}

/** Constant answer for equal-length keys; the sign is what the arbiter needs. */
function compareBytes(a, b) {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) { if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1; }
  return a.length === b.length ? 0 : (a.length < b.length ? -1 : 1);
}

/**
 * Sessions, prekeys and the device bundle for one account.
 *
 * `store` is anything with async get(key)/set(key, value)/del(key) — the
 * IndexedDB wrapper in the app, a Map in tests.
 */
export class ChatSessions {
  constructor({ identity, api, store, myUserId, verifyIncoming = null }) {
    this.identity = identity;
    this.api = api;
    this.store = store;
    this.myUserId = Number(myUserId);
    this.deviceId = null;
    /**
     * Who a HANDSHAKE claims to be from, checked before it is answered.
     *
     * The send side has asked this since K1 — a bundle we are about to
     * encrypt to is pinned or refused. The receive side never asked it, and
     * the two are not the same question. A first message carries the
     * initiator's identity DH key on the wire and X3DH is derived straight
     * from it; who sent it comes from a database row the server owns. So a
     * hostile server generates its own identity, builds a perfectly valid
     * handshake to us, files it under somebody else's name, and we open it,
     * authenticate it, and put their name on it. No key is broken — the
     * initiator's identity was simply never checked against anything.
     *
     * Given (peerUserId, peerDeviceId, identityDhPub) this answers whether
     * that DH key really belongs to that person's device. Absent, behaviour
     * is unchanged.
     */
    this.verifyIncoming = verifyIncoming;
  }

  _sessionKey(peerUserId, peerDeviceId) { return `session:${peerUserId}:${peerDeviceId}`; }

  /**
   * A message we sent exists on the server addressed to the RECIPIENT's
   * devices and to nobody else — so on reload this device could not find its
   * own sent messages, and they survived only as long as the local cache did.
   *
   * The fix is a copy addressed to ourselves. It cannot go through a ratchet:
   * a session has two ends and both would live in this one store, where
   * encrypting would advance the very chain the decrypt needs. So it is sealed
   * with a key derived from this device's own secret — no session, no state,
   * nothing to desynchronise.
   *
   * The consequence is honest and already true of everything else here: lose
   * the device and these copies are unreadable. That is what the history
   * export exists for.
   */
  /** LEGACY (SEC-07): the eternal identity-derived key. Kept only to READ
   *  copies sealed before the self-ratchet; nothing seals with it any more —
   *  one stolen identity secret used to open years of sent messages. */
  _selfKeyLegacy() {
    const secret = this.identity.dhKeyPair().secretKey;
    const input = new Uint8Array(SELF_COPY_TAG.length + secret.length);
    input.set(new TextEncoder().encode(SELF_COPY_TAG));
    input.set(secret, SELF_COPY_TAG.length);
    return nacl.hash(input).slice(0, 32);
  }

  static _selfDerive(tag, ck) {
    const t = new TextEncoder().encode(tag);
    return nacl.hash(concat(t, ck)).slice(0, 32);
  }

  /**
   * Seal our own copy with a key that exists for ONE message (SEC-07, §7д).
   *
   * The chain only moves forward: MK_n comes off the current chain key, the
   * chain key is immediately replaced by its own hash, and the old value is
   * gone. MK_n itself has to wait — the echo of this copy comes back from
   * the server later — so it sits in `pending` until the first open spends
   * it, with the skipped-key TTL and cap for echoes that never return.
   * Stealing the store today therefore opens at most a week of unreturned
   * echoes, not the years of sent mail the eternal key used to give away.
   */
  async _sealSelf(plaintext) {
    let st = await this.store.get('selfchain');
    if (!st) st = { v: 1, ck: b64(nacl.randomBytes(32)), n: 0, pending: {} };
    const now = Date.now();
    for (const [k, p] of Object.entries(st.pending)) {
      if (now - (p.at || 0) > SELF_PENDING_TTL_MS) delete st.pending[k];
    }
    const waiting = Object.keys(st.pending).map(Number).sort((a, b) => a - b);
    while (waiting.length >= MAX_SELF_PENDING) delete st.pending[waiting.shift()];

    const ck = unb64(st.ck);
    const mk = ChatSessions._selfDerive(SELF_TAG_MSG, ck);
    st.ck = b64(ChatSessions._selfDerive(SELF_TAG_CHAIN, ck));
    st.n += 1;
    st.pending[st.n] = { key: b64(mk), at: now };
    await this.store.set('selfchain', st);

    const nonce = nacl.randomBytes(24);
    const header = new Uint8Array(5);
    header[0] = MSG_SELF2;
    header[1] = (st.n >>> 24) & 0xff; header[2] = (st.n >>> 16) & 0xff;
    header[3] = (st.n >>> 8) & 0xff; header[4] = st.n & 0xff;
    return concat(header, nonce, nacl.secretbox(plaintext, nonce, mk));
  }

  _openSelfLegacy(bytes) {
    if (bytes.length < 1 + 24 + 16) return null;
    return nacl.secretbox.open(bytes.slice(25), bytes.slice(1, 25), this._selfKeyLegacy());
  }

  // ─── our own prekeys ──────────────────────────────────────────────────────

  /**
   * Publish this device and stock it with one-time prekeys.
   *
   * The signed prekey's secret is kept locally, and so is the previous one:
   * a message composed against the old prekey before we rotated must still
   * open, and the server keeps serving it for a week for that reason.
   */
  async publish({ rotate = false, label = null } = {}) {
    const sig = this.identity.keyPair();
    const dh = this.identity.dhKeyPair();

    let spk = await this.store.get('spk');
    if (!spk || rotate) {
      const next = generatePreKey();
      if (spk) await this.store.set('spkPrev', spk);
      spk = { publicKey: b64(next.publicKey), secretKey: b64(next.secretKey) };
      await this.store.set('spk', spk);
    }
    const spkPub = unb64(spk.publicKey);
    const preKeySig = signPreKey(sig.secretKey, spkPub);

    const r = await this.api.publishBundle({
      identitySig: b64(sig.publicKey),
      identityDh: b64(dh.publicKey),
      signedPreKey: spk.publicKey,
      preKeySig: b64(preKeySig),
      deviceLabel: label,
    });
    this.deviceId = r.deviceId;
    await this.store.set('deviceId', r.deviceId);

    // A session with ourselves, left over from when ensureSessions did not
    // skip this device. It never encrypted anything — our own copy is sealed,
    // not ratcheted — but it shows up in diagnostics as a session that cannot
    // be explained, and an unexplainable session is the thing this log exists
    // to make impossible.
    await this.store.del(this._sessionKey(this.myUserId, r.deviceId));
    await this.store.del(`pending:${this.myUserId}:${r.deviceId}`);

    if ((r.oneTimeRemaining ?? 0) < ONE_TIME_LOW_WATER) await this.topUpOneTimePreKeys();
    return r.deviceId;
  }

  async topUpOneTimePreKeys() {
    const held = (await this.store.get('opk')) || {};
    const fresh = [];
    for (let i = Object.keys(held).length; i < ONE_TIME_TARGET; i++) {
      const kp = generatePreKey();
      held[b64(kp.publicKey)] = b64(kp.secretKey);
      fresh.push(b64(kp.publicKey));
    }
    if (!fresh.length) return 0;
    await this.store.set('opk', held);
    await this.api.uploadOneTimePreKeys(this.deviceId, fresh);
    return fresh.length;
  }

  async _preKeySecret(publicKeyB64) {
    const held = (await this.store.get('opk')) || {};
    return held[publicKeyB64] ? unb64(held[publicKeyB64]) : null;
  }

  /** A one-time prekey is exactly that: gone once it has served. */
  async _consumeOneTime(publicKeyB64) {
    const held = (await this.store.get('opk')) || {};
    if (!held[publicKeyB64]) return;
    delete held[publicKeyB64];
    await this.store.set('opk', held);
  }

  async _signedPreKeyFor(publicKeyB64) {
    for (const slot of ['spk', 'spkPrev']) {
      const k = await this.store.get(slot);
      if (k && k.publicKey === publicKeyB64) {
        return { publicKey: unb64(k.publicKey), secretKey: unb64(k.secretKey) };
      }
    }
    return null;
  }

  // ─── sessions ─────────────────────────────────────────────────────────────

  async _loadSession(peerUserId, peerDeviceId) {
    const raw = await this.store.get(this._sessionKey(peerUserId, peerDeviceId));
    return raw ? deserializeState(raw) : null;
  }

  async _saveSession(peerUserId, peerDeviceId, state) {
    await this.store.set(this._sessionKey(peerUserId, peerDeviceId), serializeState(state));
  }

  /**
   * Start a session towards one device from its published bundle.
   *
   * `verifyIdentity` is the caller's business: it must check the bundle
   * against a TOFU-pinned key (ADR-014) and refuse otherwise. This function
   * checks only what it can — that the prekey signature matches the identity
   * key in the same bundle — which stops a corrupted bundle, not a swapped one.
   */
  async startSession(peerUserId, bundle) {
    const identitySig = unb64(bundle.identitySig);
    const identityDh = unb64(bundle.identityDh);
    const signedPreKey = unb64(bundle.signedPreKey);
    if (!verifyPreKey(unb64(bundle.preKeySig), signedPreKey, identitySig)) {
      throw new Error(`bundle for device ${bundle.deviceId} does not verify`);
    }
    const oneTime = bundle.oneTimePreKey ? unb64(bundle.oneTimePreKey) : null;

    const { sk, ephemeralPub, ad } = x3dhInitiate({
      myIdentityDh: this.identity.dhKeyPair(),
      theirIdentityDhPub: identityDh,
      theirSignedPreKeyPub: signedPreKey,
      theirOneTimePreKeyPub: oneTime,
    });
    const state = initSender(sk, signedPreKey, ad);
    // Signed once, here, where the recipient's identity is still in hand —
    // the envelope is packed later, per message, and must not need the bundle.
    const sig = nacl.sign.detached(prekeyTranscript({
      identitySigPub: this.identity.keyPair().publicKey,
      identityDhPub: this.identity.dhKeyPair().publicKey,
      ephemeralPub,
      recipientIdentityDhPub: identityDh,
      signedPreKeyPub: signedPreKey,
      oneTimePreKeyPub: oneTime,
    }), this.identity.keyPair().secretKey);
    await this._saveSession(peerUserId, bundle.deviceId, state);
    // Held until the first message so it can carry them; after that the
    // session stands on its own.
    await this.store.set(`pending:${peerUserId}:${bundle.deviceId}`, {
      ephemeralPub: b64(ephemeralPub),
      signedPreKeyPub: bundle.signedPreKey,
      oneTimePreKeyPub: bundle.oneTimePreKey || null,
      sig: b64(sig),
    });
    return state;
  }

  /**
   * Make sure there is a session with every device of this person, and return
   * the device list.
   *
   * Listing is free; fetching a bundle costs the recipient a one-time prekey.
   * So devices are listed first and bundles asked for ONLY for the ones we do
   * not already have a session with. Before this, every message fetched every
   * bundle twice — a hundred prekeys gone in about twenty-five messages, after
   * which every session fell back to the weaker handshake permanently, and the
   * request rate tripped the server's own limiter.
   */
  async ensureSessions(peerUserId, { verifyBundle } = {}) {
    const { devices } = await this.api.listDevices(peerUserId);
    const need = [];
    for (const d of devices) {
      // Never with ourselves. Our own copy is sealed, not ratcheted, so a
      // session here is dead state — and asking for our own bundle spends one
      // of our own one-time prekeys to build it.
      if (d.deviceId === this.deviceId) continue;
      if (!(await this._loadSession(peerUserId, d.deviceId))) { need.push(d.deviceId); continue; }
      // A pending from before envelopes were signed. It cannot be re-signed —
      // the recipient identity it was built against is gone — and refusing to
      // send until a human intervenes would be a dead end wearing a security
      // badge. It is a lost handshake, so it gets the lost-handshake cure:
      // archive the session, build a fresh signed one. In flight messages
      // still open from the archive.
      const pending = await this.store.get(`pending:${peerUserId}:${d.deviceId}`);
      if (pending && !pending.sig) {
        console.log('[CHAT] handshake predates signed envelopes — rebuilding the session for device',
          String(d.deviceId).slice(0, 8));
        await this.resetSession(peerUserId, d.deviceId);
        need.push(d.deviceId);
      }
    }
    if (!need.length) return { devices, opened: [], refused: [] };

    const { devices: bundles } = await this.api.getBundles(peerUserId, need);
    const opened = [];
    const refused = [];
    for (const bundle of bundles) {
      // The identity check the prekey signature CANNOT do. That signature is
      // made with the identity key inside the same bundle, so it proves the
      // bundle is self-consistent and nothing about whose bundle it is. A
      // server that answers with a device of its own passes it every time.
      //
      // Refused devices are named rather than skipped: silently sending to
      // two devices out of three is how a message reaches the attacker and
      // not the person, with nothing on screen to show for it.
      if (verifyBundle && !(await verifyBundle(peerUserId, bundle))) {
        refused.push({ deviceId: bundle.deviceId, identitySig: bundle.identitySig,
                       deviceLabel: bundle.deviceLabel || null });
        continue;
      }
      try {
        await this.startSession(peerUserId, bundle);
        opened.push(bundle.deviceId);
      } catch (e) {
        // One unusable device must not stop the others.
      }
    }
    return { devices, opened, refused };
  }

  /**
   * Encrypt one payload for every device that should receive it — the peer's
   * and this account's own others.
   *
   * Returns one entry per device. A device we cannot encrypt to is reported
   * rather than silently skipped: "delivered to 2 of 3 devices" is a fact the
   * layer above may want to show.
   */
  async encryptFor(peerUserId, plaintext, { verifyBundle } = {}) {
    const targets = [{ userId: peerUserId }];
    // Always includes us: our own other devices need the message, and this
    // device needs its own copy so a reload does not lose what it sent.
    if (Number(peerUserId) !== this.myUserId) targets.push({ userId: this.myUserId, self: true });

    const out = [];
    for (const t of targets) {
      const { devices, refused } = await this.ensureSessions(t.userId, { verifyBundle });
      const untrusted = new Set((refused || []).map(r => r.deviceId));
      for (const bundle of devices) {
        if (untrusted.has(bundle.deviceId)) {
          out.push({ userId: t.userId, deviceId: bundle.deviceId, error: 'untrusted' });
          continue;
        }
        if (t.self && bundle.deviceId === this.deviceId) {
          // Our own copy, so this device still has what it sent after a
          // reload. Sealed to ourselves rather than through a session.
          out.push({
            userId: t.userId, deviceId: bundle.deviceId,
            body: await this._sealSelf(plaintext), self: true, own: true,
          });
          continue;
        }
        const state = await this._loadSession(t.userId, bundle.deviceId);
        if (!state) {
          // ensureSessions already tried and could not — a bundle that does
          // not verify, or a device that vanished between the two calls.
          out.push({ userId: t.userId, deviceId: bundle.deviceId, error: 'no session' });
          continue;
        }
        try {
          const frame = ratchetEncrypt(state, plaintext);
          const pending = await this.store.get(`pending:${t.userId}:${bundle.deviceId}`);
          if (pending && !pending.sig) {
            // A handshake made before envelopes were signed. It cannot be
            // re-signed here — the recipient identity it was built against is
            // gone — and sending it unsigned is the hole v2 closes.
            throw new Error('handshake predates signed envelopes — reset this session');
          }
          const body = pending
            ? packPreKeyMessage({
                identitySigPub: this.identity.keyPair().publicKey,
                identityDhPub: this.identity.dhKeyPair().publicKey,
                ephemeralPub: unb64(pending.ephemeralPub),
                signedPreKeyPub: unb64(pending.signedPreKeyPub),
                oneTimePreKeyPub: pending.oneTimePreKeyPub ? unb64(pending.oneTimePreKeyPub) : null,
                sig: unb64(pending.sig),
                frame,
              })
            : packNormalMessage(frame);
          await this._saveSession(t.userId, bundle.deviceId, state);
          out.push({ userId: t.userId, deviceId: bundle.deviceId, body, self: !!t.self });
        } catch (e) {
          out.push({ userId: t.userId, deviceId: bundle.deviceId, error: e.message });
        }
      }
    }
    return out;
  }

  /**
   * Open an incoming message from one device, WITHOUT moving the ratchet.
   *
   * Returns the plaintext plus a `commit` that advances and persists the
   * session. Nothing is stored until the caller says so, and the caller is
   * expected to write the plaintext down FIRST: there is no transaction
   * spanning the archive and the key store, so the order is the guarantee.
   * Persist the ratchet first and a crash in between loses the message for
   * good — the key that opened it no longer exists anywhere. Persist the text
   * first and the same crash costs one repeated decryption, which succeeds,
   * because the session never moved.
   *
   * A prekey message may arrive when a session already exists — the sender
   * lost their state and started over. Their new session is adopted, because
   * the alternative is a conversation that can never resume.
   */
  async openMessage(peerUserId, peerDeviceId, bytes) {
    const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const fail = (reason, extra) => ({ out: null, reason, diag: extra || null });
    /**
     * Every successful open, whichever session did it, stops us advertising
     * our handshake to that device.
     *
     * One rule in one place, because writing it out per branch missed one: the
     * archived-session path had no clear, so two phones with a tangled history
     * went on attaching a handshake to every message even after the other
     * branches were fixed. The rule itself is simple enough not to need
     * restating — anything of theirs that we can open proves they can already
     * talk to us, and a handshake exists only to tell them how.
     */
    const done = (out, commit) => ({
      out,
      commit: async () => {
        if (commit) await commit();
        await this.store.del(`pending:${peerUserId}:${peerDeviceId}`);
      },
    });

    // Our own copy of something we sent — no session, but since SEC-07 there
    // IS something to spend: the per-message key, destroyed by the commit
    // AFTER the plaintext is written down. The same order as sessions: a
    // crash in between costs one repeated open, never the message.
    if (raw.length && raw[0] === MSG_SELF2) {
      if (raw.length < 5 + 24 + 16) return fail('own copy is too short to be one');
      const n = ((raw[1] << 24) | (raw[2] << 16) | (raw[3] << 8) | raw[4]) >>> 0;
      const st = await this.store.get('selfchain');
      const p = st && st.pending && st.pending[n];
      if (!p) return fail('own copy key already spent — opened before, or its echo took longer than a week');
      const out = nacl.secretbox.open(raw.slice(29), raw.slice(5, 29), unb64(p.key));
      if (!out) return fail('own copy did not open — this device is not the one that sealed it');
      return done(out, async () => {
        const cur = await this.store.get('selfchain');
        if (cur && cur.pending && cur.pending[n]) {
          delete cur.pending[n];
          await this.store.set('selfchain', cur);
        }
      });
    }
    // The legacy era: one eternal key. Read-only — nothing seals this way.
    if (raw.length && raw[0] === MSG_SELF) {
      const self = this._openSelfLegacy(raw);
      return self ? done(self) : fail('own copy did not open — this device is not the one that sealed it');
    }
    const msg = unpackMessage(raw);
    if (!msg) return fail('frame is neither prekey nor normal', { len: raw.length, first: raw[0] });

    if (msg.type === MSG_PREKEY) {
      const existing = await this._loadSession(peerUserId, peerDeviceId);
      if (existing) {
        const diag = {};
        const out = ratchetDecrypt(existing, msg.frame, diag);
        if (out) {
          return done(out, async () => {
            await this._saveSession(peerUserId, peerDeviceId, existing);
          });
        }
      }
      // Before anything else: this may be a message that was already in
      // flight under a session we replaced — or one that keeps arriving under
      // a handshake that LOST the tiebreak below. Either way it is a real
      // message and the archive is what opens it. This try sits before the
      // replay check on purpose: later frames of a losing handshake carry the
      // same ephemeral as its first one, and the marker must not eat them.
      const archived = await this._tryArchived(peerUserId, peerDeviceId, msg.frame);
      if (archived) {
        console.log('[CHAT] opened with an archived session — it was in flight when we rebuilt');
        return done(archived.out, archived.commit);
      }
      // Two things look identical here and must not be treated alike: the
      // sender genuinely lost their state and started over, or somebody is
      // replaying a handshake we have already answered. Answering it again
      // would build a session on top of the working one — a replayed message
      // that resets a live conversation.
      //
      // The ephemeral key tells them apart: a new session carries a new one,
      // a replay carries the one we consumed.
      const seenEphemeral = await this.store.get(`handshake:${peerUserId}:${peerDeviceId}`);
      if (seenEphemeral && seenEphemeral === b64(msg.ephemeralPub)) {
        return fail('this message was already opened on the session it once built');
      }

      // From here on we are about to ANSWER the handshake, and v1 has nothing
      // to answer with: no signature, no initiator to hold responsible. There
      // are no old clients to keep compatible with — refusing is the fix.
      if (msg.v !== 2) {
        return fail('рукопожатие старого формата, без подписи инициатора — обновите приложение и сбросьте шифрование', { envelope: 1 });
      }
      // The envelope's own arithmetic, before any identity question. Not a
      // callback and not optional: a check nobody passes is a check that does
      // not exist, and this file has already paid for learning that.
      const transcript = prekeyTranscript({
        identitySigPub: msg.identitySigPub,
        identityDhPub: msg.identityDhPub,
        ephemeralPub: msg.ephemeralPub,
        recipientIdentityDhPub: this.identity.dhKeyPair().publicKey,
        signedPreKeyPub: msg.signedPreKeyPub,
        oneTimePreKeyPub: msg.oneTimePreKeyPub,
      });
      if (!nacl.sign.detached.verify(transcript, msg.sig, msg.identitySigPub)) {
        return fail('подпись инициатора не сходится — рукопожатие отвергнуто', { badSignature: true });
      }

      // Whose handshake this is, before a single X3DH term is computed.
      // Refusing after deriving the secret would be too late in the only way
      // that matters: the plaintext would already be on screen. The signing
      // key travels in the envelope now, so the verifier can compare it to
      // the pin even when the server is unreachable.
      if (this.verifyIncoming) {
        const verdict = await this.verifyIncoming(peerUserId, peerDeviceId, msg.identityDhPub, msg.identitySigPub);
        if (verdict && verdict.ok === false) {
          return fail(verdict.reason || 'handshake does not come from who it says', { impostor: true, ...verdict });
        }
      }

      const spk = await this._signedPreKeyFor(b64(msg.signedPreKeyPub));
      if (!spk) return fail('handshake names a signed prekey we no longer hold — rotated out of the grace window');
      const opkB64 = msg.oneTimePreKeyPub ? b64(msg.oneTimePreKeyPub) : null;
      const opkSecret = opkB64 ? await this._preKeySecret(opkB64) : null;
      if (opkB64 && !opkSecret) return fail('handshake names a one-time prekey we do not hold — already used, or never ours');

      const { sk, ad } = x3dhRespond({
        myIdentityDh: this.identity.dhKeyPair(),
        mySignedPreKey: spk,
        myOneTimePreKey: opkSecret ? { secretKey: opkSecret } : null,
        theirIdentityDhPub: msg.identityDhPub,
        theirEphemeralPub: msg.ephemeralPub,
      });
      const state = initReceiver(sk, spk, ad);
      const diag = {};
      const out = ratchetDecrypt(state, msg.frame, diag);
      if (!out) return fail('fresh handshake accepted but its first message did not open', diag);

      /**
       * Both sides started at once — each holds an unacknowledged handshake
       * of its own, and "archive mine, adopt theirs" is symmetric: both
       * yield, the sessions swap, and each side lives out of an archive that
       * expires in a week. The symptom in the database was ten messages in a
       * row, every one a handshake, with the client logs perfectly quiet.
       *
       * The arbiter is a comparison both sides compute identically, whatever
       * the delivery order: THE LARGER HANDSHAKE EPHEMERAL WINS. We know ours
       * from `pending`, theirs is in the envelope. The loser's session is
       * still answered — its first message and anything else in flight under
       * it must open — but it goes straight to the archive; the winner stays
       * current on both ends, so one crossed exchange converges.
       */
      // A pending without a signature is from before envelopes were signed;
      // it can never be advertised again (the next send rebuilds it), so it
      // must never win the tiebreak either — the incoming handshake is the
      // only one of the two with a future.
      const ourPending = await this.store.get(`pending:${peerUserId}:${peerDeviceId}`);
      if (ourPending && ourPending.sig && ourPending.ephemeralPub && existing) {
        const cmp = compareBytes(msg.ephemeralPub, unb64(ourPending.ephemeralPub));
        if (cmp === 0) return fail('their handshake carries our own ephemeral — refused');
        if (cmp < 0) {
          // Ours wins. Their session is read-only from birth. Note what done()
          // does below: it drops our pending — and that is still correct,
          // because the message advertising our handshake is already in their
          // queue; stopping here only stops REPEATING it.
          return done(out, async () => {
            await this._archiveState(peerUserId, peerDeviceId, state);
            await this.store.set(`handshake:${peerUserId}:${peerDeviceId}`, b64(msg.ephemeralPub));
            if (opkB64) await this._consumeOneTime(opkB64);
            if (this.deviceId) this.topUpOneTimePreKeys().catch(() => {});
          });
        }
        // Theirs wins — fall through and adopt it, exactly as when we had
        // nothing pending at all.
      }
      return done(out, async () => {
        // Whatever we had for this device goes to the archive first. Adopting
        // over the top of it is what left two phones each holding the session
        // the other had discarded.
        await this._archiveCurrent(peerUserId, peerDeviceId);
        await this._saveSession(peerUserId, peerDeviceId, state);
        // Which handshake this session was built from, so the same one arriving
        // again is recognised as a repeat rather than answered twice.
        await this.store.set(`handshake:${peerUserId}:${peerDeviceId}`, b64(msg.ephemeralPub));
        if (opkB64) await this._consumeOneTime(opkB64);
        // Used one up; keep the shelf stocked.
        if (this.deviceId) this.topUpOneTimePreKeys().catch(() => {});
      });
    }

    const state = await this._loadSession(peerUserId, peerDeviceId);
    if (!state) {
      // Maybe it belongs to the session we just replaced.
      const archived = await this._tryArchived(peerUserId, peerDeviceId, msg.frame);
      if (archived) {
        console.log('[CHAT] opened with an archived session — no current one exists');
        return done(archived.out, archived.commit);
      }
      // The other side believes a session exists and has stopped attaching the
      // handshake. Nothing they send will ever open until they are told to
      // start again (astrocall-kwk).
      return fail('no session for this device, and the message carries no handshake');
    }
    const diag = {};
    const out = ratchetDecrypt(state, msg.frame, diag);
    if (!out) {
      // Perhaps it was sent under the session this one replaced — a message
      // already on its way when we decided the old one was dead.
      const archived = await this._tryArchived(peerUserId, peerDeviceId, msg.frame);
      if (archived) {
        console.log('[CHAT] opened with an archived session — it was in flight when we rebuilt');
        return done(archived.out, archived.commit);
      }
      return fail('session exists but the chains do not agree', diag);
    }
    return done(out, async () => {
      await this._saveSession(peerUserId, peerDeviceId, state);
    });
  }

  /**
   * Open and commit in one step.
   *
   * Kept for callers that hold no archive of their own — tests, and the export
   * path. The intake pipeline uses openMessage directly so it can write the
   * plaintext down before the ratchet moves.
   */
  async decryptFrom(peerUserId, peerDeviceId, bytes) {
    const { out, commit, reason, diag } = await this.openMessage(peerUserId, peerDeviceId, bytes);
    if (!out) {
      console.warn('[CHAT] decrypt failed from user', peerUserId, 'device', String(peerDeviceId).slice(0, 8),
        '—', reason, diag ? JSON.stringify(diag) : '');
      return null;
    }
    await commit();
    return out;
  }

  /** Forget a session — the next message from that device starts a new one. */
  async resetSession(peerUserId, peerDeviceId) {
    // Archived, not deleted. Signal does the same, and for a reason we have
    // already paid for: at the moment a session is judged dead there are
    // usually messages in flight that were encrypted under it. Deleting the
    // state makes those unreadable for ever; keeping it lets them open while
    // everything new goes through the session that replaces it.
    await this._archiveCurrent(peerUserId, peerDeviceId);
    await this.store.del(this._sessionKey(peerUserId, peerDeviceId));
    await this.store.del(`pending:${peerUserId}:${peerDeviceId}`);
  }

  /**
   * Put the session we are about to stop using somewhere it can still be read.
   *
   * The reason it is never simply overwritten is the same one Signal archives
   * for, and it bites hardest when both sides start at once. Each had built a
   * session and each adopted the other's handshake — so each ended up on the
   * session the OTHER had just thrown away, and nothing either sent could be
   * opened by anybody. Keeping the old one means both sessions stay readable
   * until the ratchet converges on one.
   */
  async _archiveCurrent(peerUserId, peerDeviceId) {
    const current = await this.store.get(this._sessionKey(peerUserId, peerDeviceId));
    if (!current) return;
    await this._archiveSerialized(peerUserId, peerDeviceId, current);
  }

  /**
   * Archive a session that never was current: the one built for a handshake
   * that lost the simultaneous-start tiebreak. It must be able to open what
   * is in flight under it, and it must never be sent on — which is exactly
   * what the archive means.
   */
  async _archiveState(peerUserId, peerDeviceId, state) {
    await this._archiveSerialized(peerUserId, peerDeviceId, serializeState(state));
  }

  async _archiveSerialized(peerUserId, peerDeviceId, serialized) {
    const key = `archived:${peerUserId}:${peerDeviceId}`;
    const held = (await this.store.get(key)) || [];
    const next = [{ state: serialized, at: Date.now() }, ...held].slice(0, MAX_ARCHIVED_SESSIONS);
    await this.store.set(key, next);
  }

  /**
   * Sessions this device has replaced but not forgotten.
   *
   * Read-only in spirit: they exist to open what was already in flight, never
   * to send. An archived session that opens a message is written back, so the
   * next stray message from the same chain opens too.
   */
  async _tryArchived(peerUserId, peerDeviceId, frame) {
    const key = `archived:${peerUserId}:${peerDeviceId}`;
    const held = (await this.store.get(key)) || [];
    if (!held.length) return null;
    const fresh = held.filter(h => Date.now() - (h.at || 0) < ARCHIVED_TTL_MS);
    for (let i = 0; i < fresh.length; i++) {
      let state;
      try { state = deserializeState(fresh[i].state); } catch (e) { continue; }
      const out = ratchetDecrypt(state, frame);
      if (!out) continue;
      return {
        out,
        commit: async () => {
          const updated = fresh.slice();
          updated[i] = { state: serializeState(state), at: fresh[i].at };
          await this.store.set(key, updated);
        },
      };
    }
    if (fresh.length !== held.length) await this.store.set(key, fresh);
    return null;
  }
}
